import { Fragment, HTMLProps, useEffect, useMemo, useRef, useState } from "react";

import date from "date-and-time";
import { useNavigate } from "react-router-dom";

import { CameraIcon, CheckIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { StarIcon } from "@heroicons/react/24/solid";
import {
  createColumnHelper,
  SortingState,
} from "@tanstack/react-table";

import useLeads from "../../../data/useLeads";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import { ILead, LeadQualified, LeadSource, LeadStatus } from "../../../models/Lead";
import useNotification from "../../notifications/useNotifications";
import DuplicateVehicleBadge from "../DuplicateVehicleBadge";
import MarkCollectedModal from "../MarkCollectedModal";
import openInNewTab from "../../../utils/openInNewTab";
import { enteredRegIfDifferent } from "../../../utils/leadRegs";
import QuoteOwnerPill from "../../shared/Quotes/QuoteOwnerPill";
import PagedTable from "../../shared/Tables/PagedTable";

function IndeterminateCheckbox({
  indeterminate,
  className = "",
  ...rest
}: { indeterminate?: boolean } & HTMLProps<HTMLInputElement>) {
  const ref = useRef<HTMLInputElement>(null!);

  useEffect(() => {
    if (typeof indeterminate === "boolean") {
      ref.current.indeterminate = !rest.checked && indeterminate;
    }
  }, [ref, indeterminate]);

  return (
    <input
      type="checkbox"
      ref={ref}
      className={className + "p-3 cursor-pointer"}
      {...rest}
    />
  );
}

const colHelper = createColumnHelper<ILead>();

const baseCols = [
  colHelper.display({
    id: "image",
    cell: (info) => (
      <span className="flex justify-center relative">
        {info.row.original.vehicle?.vehicleImageUrl ? (
          <img
            className="w-20 h-14 sm:w-32 sm:h-20 object-contain"
            src={`${
              info.row.original.vehicle != null
                ? info.row.original.vehicle.vehicleImageUrl
                : null
            }`}
          />
        ) : (
          <CameraIcon className="w-14 text-gray-600" />
        )}
        {info.row.original.vehicle?.vehiclePremium && (
          <StarIcon className="w-8 h-8 text-amber-500 absolute -top-2 -right-2" />
        )}
      </span>
    ),
  }),
  colHelper.accessor("contact.fullname", {
    header: "Name",
    sortingFn: "auto",
    // Fullname is computed on the Contact entity, not a DB column, so EF can't order by it.
    meta: { sortKey: "contact.firstName" },
  }),
  colHelper.accessor("contact.telephone1", {
    header: "Telephone",
    enableSorting: false,
  }),
  colHelper.accessor("vehicle.postcode", {
    header: "Postcode",
    enableSorting: false,
  }),
  colHelper.accessor("vehicle.vrm", {
    header: "VRM",
    enableSorting: false,
    // The duplicate marker sits with the reg because that is what it is about: another booking
    // already holds this vehicle. Hover gives the reason; the lead itself carries the full banner.
    cell: (info) => {
      const entered = enteredRegIfDifferent(info.row.original);
      return (
        <span className="whitespace-nowrap">
          {info.getValue()}
          <DuplicateVehicleBadge lead={info.row.original} />
          {entered && <span className="block text-xs text-gray-500">entered as {entered}</span>}
        </span>
      );
    },
  }),
  colHelper.accessor("status", {
    header: "Status",
    enableSorting: false,
    // Call-centre leads use Call List One (10), which models/LeadStatus does not list.
    cell: (info) => <span>{(info.getValue() as number) === 10 ? "Call List One" : LeadStatus[info.getValue()]}</span>,
  }),
  colHelper.accessor("website", {
    header: "Source",
    enableSorting: false,
    cell: (info) => <span>{LeadSource[info.getValue()]}</span>,
  }),
  colHelper.accessor("vehicle.scheduledCollectionOn", {
    header: "Collection",
    sortingFn: "auto",
    cell: (collection) => (
      <span>
        {collection.getValue() !== null
          ? date.format(new Date(collection.getValue()!), "DD/MM/YYYY")
          : "Not Set"}
      </span>
    ),
  }),
];

// Statuses where the vehicle is already in, or the lead is closed, so there is nothing to mark.
const notCollectable = [
  LeadStatus["Collected"],
  LeadStatus["Awaiting Payment"],
  LeadStatus["Awaiting COD"],
  LeadStatus["Completed"],
  LeadStatus["Cancelled"],
];

const buildMarkCollectedCol = (onMarkCollected: (lead: ILead) => void) =>
  colHelper.display({
    id: "markCollected",
    header: "",
    cell: (info) => {
      const lead = info.row.original;
      const collectedOn = lead.vehicle?.collectedOn;

      if (collectedOn) {
        return (
          <span className="inline-flex items-center gap-1 whitespace-nowrap font-medium text-green-700">
            <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" />
            Collected {date.format(new Date(collectedOn), "DD/MM/YYYY")}
          </span>
        );
      }

      if (!lead.vehicle || notCollectable.includes(lead.status)) return null;

      return (
        <button
          type="button"
          aria-label={`Mark ${lead.vehicle.vrm ?? "vehicle"} collected`}
          onClick={(e) => {
            // The row itself opens the lead in a new tab.
            e.stopPropagation();
            onMarkCollected(lead);
          }}
          className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-green-700 shadow-sm hover:bg-green-50 focus:outline-none focus:ring-1 focus:ring-primary-500"
        >
          <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" />
          Mark collected
        </button>
      );
    },
  });

const savExtraCols = [
  colHelper.accessor("customerId", {
    header: "Customer",
    sortingFn: "auto",
    cell: (collection) => (<QuoteOwnerPill customerId={collection.getValue()} showLabel={false} />),
  }),
]

export default function LeadsTable({ 
  isSav,
  customUrlQuery,
  externalQuery,
  additionalBody
}: { isSav: boolean, customUrlQuery?: any, externalQuery?: any, additionalBody?: any }) {
  const navigate = useNavigate();
  const { bulkUpdate, bulkComplete, update } = useLeads(true);
  const { addNotification } = useNotification();
  const { saveHandlers } = useDefaultCRUDHandlers("Leads");
  const [updateOpen, setUpdateOpen] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [selectedStatus, setSelectedStatus] = useState("0");
  const [collectLead, setCollectLead] = useState<ILead | null>(null);
  const [collectOpen, setCollectOpen] = useState(false);

  const columns = useMemo(() => {
    const markCollected = buildMarkCollectedCol((lead) => {
      setCollectLead(lead);
      setCollectOpen(true);
    });
    return [...baseCols, ...(isSav ? savExtraCols : []), markCollected];
  }, [isSav]);

  const handleClassNames = (lead: ILead) => {
    let classNames = "";

    if (!lead.vehicle?.found) classNames = "bg-gray-200 ";
    if (
      lead.website === 6
    )
      classNames = "bg-green-200/80"
    return classNames;
  };

  return (
    <>
      <MarkCollectedModal open={collectOpen} setOpen={setCollectOpen} lead={collectLead} />
      <div className="bg-white shadow rounded-lg mt-4 p-4">
        <PagedTable<ILead>
          key={"/lead/paginated"}
          url={"/lead/paginated"}
          columns={columns}
          showTotal={true}
          initialSort={[{ id: "createdDate", desc: true }]}
          // cellPadding="px-[0px] py-[0px]"
          fontSize="text-xs"
          containerClassName="max-h-[56vh] overflow-y-scroll"
          condensedView={true}
          rowColor={handleClassNames}
          onRowClick={(lead: ILead) => openInNewTab(`leads/${lead.id}`)}
          customUrlQuery={customUrlQuery}
          externalQuery={externalQuery}
          additionalBody={additionalBody}
          queryKey={"leadsWithFilters"}
        />
      </div>
    </>
  );
}
